import React from "react";
import TitleBar from "../../components/navbar/TitleBar";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu";
import Footer from "../../components/common/Footer";
import SubTitle from "../../components/common/SubTitle";
import SearchFilter from "../../components/common/SearchFilter";
import { Suspense, useState, useEffect, useMemo } from "react";
import SchoolDynamicTable from "../../components/createForm/SchoolDynamicTable";
import InactiveInventoryTable from "../../components/common/InactiveInventoryTable";
import CreateStudentForm from "../../components/createForm/CreateStudentForm";
import axios from "axios";
import config from "../../config";
import { Snackbar, Alert } from "@mui/material";
import { fetchStudentById } from "../../components/createForm/CreateStudentForm.api";
import { Box } from "@mui/material";
import StudentFormSkeleton from "./StudentFormSkeleton";
import { getVehicleAssignmentDetails } from "../../components/createForm/CreateStudentForm.helpers";
import { useTranslation } from "react-i18next";
import { useUser } from "../login/UserContextPersistent";

export default function SchoolBusStudents() {
  const { t } = useTranslation();
  const { organizationInactive } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [studentData, setStudentData] = useState([]);
  const [organizationVehicles, setOrganizationVehicles] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedStudentData, setExpandedStudentData] = useState(null);
  const [isExpandedStudentLoading, setIsExpandedStudentLoading] =
    useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const headers = [
    { key: "Names", value: "Names" },
    { key: "Grades", value: "Grades" },
    { key: "ID's", value: "ID's" },
    { key: "NFC Card", value: "NFC Card" },
    { key: "Morning", value: "Morning" },
    { key: "Evening", value: "Evening" },
    { key: "Routes", value: "Routes" },
    { key: "Vehicle No.", value: "Vehicle No." },
    { key: "Attendants", value: "Attendants" },
    { key: "Pick-up", value: "Pick-up" },
    { key: "Drop", value: "Drop" },
    { key: "actions", value: "" },
  ];

  const [filters, setFilters] = useState([
    {
      id: "grades",
      label: t("Grade"),
      options: [],
    },
    {
      id: "routes",
      label: t("Routes"),
      options: [],
    },
  ]);

  useEffect(() => {
    try {
      const userStateString = localStorage.getItem("userState");
      if (userStateString) {
        const userState = JSON.parse(userStateString);
        const orgId = userState.user?.organizationId;

        if (orgId) {
          setOrganizationId(orgId);
        } else {
          setAlert({
            open: true,
            message: t("Organization ID not found"),
            severity: "error",
          });
        }
      }
    } catch (err) {
      console.error("Error reading user state:", err);
      setAlert({
        open: true,
        message: t("Failed to load user information"),
        severity: "error",
      });
    }
  }, [t]);

  useEffect(() => {
    if (organizationId) {
      fetchStudents();
      fetchFiltersData();
    }
  }, [organizationId]);

  const fetchImage = async (imageUrl) => {
    try {
      const response = await axios.get(imageUrl, {
        responseType: "arraybuffer",
        withCredentials: true,
      });
      const blob = new Blob([response.data], { type: "image/jpeg" });
      const imageDataUrl = URL.createObjectURL(blob);
      return imageDataUrl;
    } catch (error) {
      console.error(`Error fetching image from ${imageUrl}:`, error.message);
      return null;
    }
  };

  const fetchFiltersData = async () => {
    try {
      const routesResponse = await axios.get(
        `${config.baseURL}/route/organization?organizationId=${organizationId}`,
        { withCredentials: true },
      );

      if (routesResponse.data) {
        const routes = Array.isArray(routesResponse.data)
          ? routesResponse.data
          : [];
        const routeOptions = routes.map((route) => ({
          value: route.name,
          label: route.name,
        }));

        setFilters((prevFilters) =>
          prevFilters.map((filter) =>
            filter.id === "routes"
              ? { ...filter, options: routeOptions }
              : filter,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  const fetchStudents = async () => {
    setIsLoading(true);
    try {
      const [studentsResponse, vehiclesResponse] = await Promise.all([
        axios.get(`${config.baseURL}/student/organization`, {
          params: { organizationId },
          withCredentials: true,
        }),
        axios.get(`${config.baseURL}/vehicle/organization`, {
          withCredentials: true,
        }),
      ]);

      if (studentsResponse.data) {
        const students = Array.isArray(studentsResponse.data)
          ? studentsResponse.data
          : [];
        const vehicles = Array.isArray(vehiclesResponse.data)
          ? vehiclesResponse.data
          : [];
        const vehiclesById = new Map(
          vehicles
            .filter((vehicle) => vehicle?.id)
            .map((vehicle) => [Number(vehicle.id), vehicle]),
        );

        setOrganizationVehicles(vehicles);

        // Populate grades filter options from student data
        const uniqueGrades = [
          ...new Set(students.map((s) => s.grade).filter(Boolean)),
        ].sort();
        const gradeOptions = uniqueGrades.map((grade) => ({
          value: grade,
          label: grade,
        }));
        setFilters((prevFilters) =>
          prevFilters.map((filter) =>
            filter.id === "grades"
              ? { ...filter, options: gradeOptions }
              : filter,
          ),
        );

        const transformedData = await Promise.all(
          students.map((student) => transformStudentRow(student, vehiclesById)),
        );

        setStudentData(transformedData);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const formatTripStatus = (status) => {
    switch (String(status || "").toUpperCase()) {
      case "BOARDED":
        return t("Boarded");
      case "DROPPED":
        return t("Dropped");
      default:
        return t("Absent");
    }
  };

  const transformStudentRow = async (student, vehiclesById = new Map()) => {
    const normalizedVehicleId = Number(
      student.vehicle?.id ?? student.vehicleId,
    );
    const assignedVehicle =
      (!Number.isNaN(normalizedVehicleId) && normalizedVehicleId
        ? vehiclesById.get(normalizedVehicleId)
        : null) ||
      student.vehicle ||
      null;
    const vehicleAssignmentDetails =
      getVehicleAssignmentDetails(assignedVehicle);
    const vehicleNumberDisplay =
      student.vehicle?.VehicleNumber ||
      assignedVehicle?.VehicleNumber ||
      t("Not Assigned");
    const routeDisplayName =
      student.route?.name || assignedVehicle?.route?.name || t("Not Assigned");
    const driverDisplayName = student.driver
      ? `${student.driver.first_name} ${student.driver.last_name}`.trim()
      : vehicleAssignmentDetails.driverName || t("Not Assigned");
    const attendantDisplayName = student.employee
      ? `${student.employee.firstName} ${student.employee.lastName}`.trim()
      : vehicleAssignmentDetails.attendantName || t("Not Assigned");

    const profilePicDoc = student.documents?.find(
      (doc) => doc.name === "StudentImage",
    );

    let profilePictureUrl = null;
    if (profilePicDoc) {
      const imageUrlPath = `${config.baseURL}/upload/image?fileName=${
        profilePicDoc.location
      }&_=${Date.now()}`;

      try {
        profilePictureUrl = await fetchImage(imageUrlPath);
      } catch (error) {
        console.error(
          `Failed to fetch image for student ${student.id}:`,
          error,
        );
        profilePictureUrl = null;
      }
    }

    return {
      Names: `${student.firstName} ${
        student.lastName === "." ? "" : student.lastName
      }`.trim(),
      Grades: student.grade
        ? student.section && student.section !== "."
          ? `${student.grade} - ${student.section}`
          : student.grade
        : t("N/A"),
      "ID's": student.studentId || t("N/A"),
      "NFC Card": student.nfcUid || t("No card"),
      Morning: formatTripStatus(student.morningStatus),
      Evening: formatTripStatus(student.eveningStatus),
      "Vehicle No.": vehicleNumberDisplay,
      Routes: routeDisplayName,
      Attendants: attendantDisplayName,
      "Pick-up": student.pickupStop?.name || t("Not Assigned"),
      Drop: student.dropStop?.name || t("Not Assigned"),
      profilePicture: profilePictureUrl,
      fullData: {
        ...student,
        vehicle: assignedVehicle,
        fullName: `${student.firstName} ${
          student.lastName === "." ? "" : student.lastName
        }`.trim(),
        sec: student.section,
        profileImage: profilePictureUrl,
        routeId:
          student.route?.id ??
          student.routeId ??
          assignedVehicle?.route?.id ??
          assignedVehicle?.routeId ??
          "",
        vehicleId:
          student.vehicle?.id ?? student.vehicleId ?? assignedVehicle?.id ?? "",
        pickupStopId:
          student.pickupStop?.id ??
          student.pickupStop?.stopId ??
          student.pickupStopId ??
          "",
        pickupStopName: student.pickupStop?.name || "",
        dropStopId:
          student.dropStop?.id ??
          student.dropStop?.stopId ??
          student.dropStopId ??
          "",
        dropStopName: student.dropStop?.name || "",
        parentId: student.parent?.parentId || "",
        parentName: student.parent
          ? `${student.parent.firstName} ${
              student.parent.lastName === "." ? "" : student.parent.lastName
            }`.trim()
          : "",
        parentPhone: student.parent?.phoneNumber || "",
        nfcUid: student.nfcUid || "",
        driverId:
          student.driver?.id ||
          student.driverId ||
          vehicleAssignmentDetails.driverId ||
          null,
        driverName: driverDisplayName,
        employeeId:
          student.employee?.id ||
          student.employeeId ||
          vehicleAssignmentDetails.employeeId ||
          null,
        attendantName: attendantDisplayName,
      },
    };
  };

  const fetchExpandedStudentData = async (studentId) => {
    if (!studentId) return null;

    setIsExpandedStudentLoading(true);
    try {
      const selectedStudent = await fetchStudentById(studentId);
      if (!selectedStudent) {
        return null;
      }

      const vehiclesById = new Map(
        organizationVehicles
          .filter((vehicle) => vehicle?.id)
          .map((vehicle) => [Number(vehicle.id), vehicle]),
      );
      const transformedStudent = await transformStudentRow(
        selectedStudent,
        vehiclesById,
      );
      setExpandedStudentData(transformedStudent.fullData);
      return transformedStudent.fullData;
    } catch (error) {
      console.error("Error fetching expanded student details:", error);
      return null;
    } finally {
      setIsExpandedStudentLoading(false);
    }
  };

  const handleFilterChange = (values) => {
    setFilterValues(values);
  };

  const handleCreateStudent = () => {
    setShowCreateForm(true);
    setExpandedRow(null);
    setExpandedStudentData(null);
  };
  const handleCloseForm = () => {
    setShowCreateForm(false);
    // Refresh the student list when form closes (after successful save)
    fetchStudents();
  };

  const splitFullName = (fullName) => {
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0];
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : ".";
    return { firstName, lastName };
  };

  const handleSubmitStudent = async (formData) => {
    try {
      const { firstName, lastName } = splitFullName(formData.fullName);

      const apiPayload = {
        firstName,
        lastName,
        grade: formData.grade,
        section: formData.sec,
        studentId: formData.studentId,
        classTeacher: formData.classTeacher,
        parentId: formData.parentId,
        routeId: formData.routeId ? Number(formData.routeId) : null,
        vehicleId: formData.vehicleId ? Number(formData.vehicleId) : null,
        pickupStopId: formData.pickupStopId
          ? Number(formData.pickupStopId)
          : null,
        dropStopId: formData.dropStopId ? Number(formData.dropStopId) : null,
        driverId: formData.driverId ? Number(formData.driverId) : null,
        employeeId: formData.employeeId ? Number(formData.employeeId) : null,
      };

      const response = await axios.post(
        `${config.baseURL}/student`,
        apiPayload,
        {
          withCredentials: true,
        },
      );

      const studentId = response.data.student?.id || response.data.id;

      // Return the student ID so the form can upload the image
      // DON'T call fetchStudents here - it will be called when form closes
      return studentId;
    } catch (error) {
      console.error("Error creating student:", error);
      console.error("Error details:", error.response?.data);
      throw error;
    }
  };

  const handleUpdateStudent = async (formData) => {
    try {
      const { firstName, lastName } = splitFullName(formData.fullName);

      const apiPayload = {
        id: formData.id,
        firstName,
        lastName,
        grade: formData.grade,
        section: formData.sec,
        studentId: formData.studentId,
        classTeacher: formData.classTeacher,
        parentId: formData.parentId,
        routeId: formData.routeId ? Number(formData.routeId) : null,
        vehicleId: formData.vehicleId ? Number(formData.vehicleId) : null,
        pickupStopId: formData.pickupStopId
          ? Number(formData.pickupStopId)
          : null,
        dropStopId: formData.dropStopId ? Number(formData.dropStopId) : null,
        driverId: formData.driverId ? Number(formData.driverId) : null,
        employeeId: formData.employeeId ? Number(formData.employeeId) : null,
      };

      await axios.put(`${config.baseURL}/student`, apiPayload, {
        withCredentials: true,
      });

      await fetchStudents();
      setExpandedRow(null);
    } catch (error) {
      console.error("Error updating student:", error);
      console.error("Error details:", error.response?.data);
      throw error;
    }
  };

  const handleDeleteStudent = async () => {
    if (expandedRow === null) return;

    const studentToDelete =
      expandedStudentData || studentData[expandedRow]?.fullData;

    try {
      await axios.delete(`${config.baseURL}/student`, {
        data: {
          studentId: studentToDelete.id,
          organizationId: organizationId,
        },
        withCredentials: true,
      });
      setExpandedRow(null);
      setExpandedStudentData(null);

      await fetchStudents();
    } catch (error) {
      console.error("Error deleting student:", error);
      setAlert({
        open: true,
        message: t("Failed to delete student"),
        severity: "error",
      });
    }
  };

  const handleRowClick = async (rowIndex) => {
    if (expandedRow === rowIndex) {
      setExpandedRow(null);
      setExpandedStudentData(null);
      return;
    }

    const selectedRow = filteredData[rowIndex];
    setExpandedRow(rowIndex);
    setShowCreateForm(false);
    setExpandedStudentData(selectedRow?.fullData || null);

    if (selectedRow?.fullData?.id) {
      await fetchExpandedStudentData(selectedRow.fullData.id);
    }
  };

  const handleCloseExpandedForm = () => {
    setExpandedRow(null);
    setExpandedStudentData(null);
    fetchStudents();
  };

  const handleExpandedStudentImageDelete = () => {
    if (expandedRow === null) return;

    setExpandedStudentData((prev) =>
      prev ? { ...prev, profileImage: null } : prev,
    );

    setStudentData((prev) =>
      prev.map((student, index) =>
        index === expandedRow
          ? {
              ...student,
              profilePicture: null,
              fullData: {
                ...student.fullData,
                profileImage: null,
              },
            }
          : student,
      ),
    );
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const filteredData = useMemo(() => {
    let filtered = [...studentData];

    if (searchQuery) {
      filtered = filtered.filter((student) =>
        Object.entries(student).some(([key, value]) => {
          if (key === "fullData") return false;
          return String(value)
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        }),
      );
    }

    if (filterValues.grades) {
      filtered = filtered.filter((student) =>
        student.Grades.startsWith(filterValues.grades),
      );
    }

    if (filterValues.routes) {
      filtered = filtered.filter(
        (student) => student.Routes === filterValues.routes,
      );
    }

    return filtered;
  }, [searchQuery, filterValues, studentData]);

  const renderExpandedRow = (row, rowIndex) => {
    if (isExpandedStudentLoading && expandedRow === rowIndex) {
      return <StudentFormSkeleton />;
    }

    // If expandedRow was cleared (after delete), don't render anything
    if (expandedRow !== rowIndex) return null;

    const baseData = expandedStudentData || row.fullData;

    const initialData = {
      ...baseData,
      profileImage:
        baseData.profileImage ||
        row.profilePicture ||
        row.fullData?.profileImage,
    };

    return (
      <CreateStudentForm
        onClose={handleCloseExpandedForm}
        onSubmit={handleUpdateStudent}
        onDelete={handleDeleteStudent}
        onProfileImageDelete={handleExpandedStudentImageDelete}
        onNotice={(message, severity = "warning") =>
          setAlert({ open: true, message, severity })
        }
        initialData={initialData}
        isEdit={true}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
      }}
    >
      <Box>
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseAlert}
            severity={alert.severity}
            sx={{ width: "100%" }}
          >
            {alert.message}
          </Alert>
        </Snackbar>

        <TitleBar />
        <SchoolBusNavMenu />
        <SubTitle
          title={t("Students")}
          add={!organizationInactive ? t("+ Create Students") : ""}
          onAddClick={!organizationInactive ? handleCreateStudent : null}
        />
        <div className="table_container">
          {organizationInactive ? (
            <InactiveInventoryTable
              headers={headers}
              data={filteredData}
              loading={isLoading}
              emptyMessage={t("noDataFound")}
              itemLabel="students"
            />
          ) : (
            <>
          {showCreateForm && (
            <CreateStudentForm
              onClose={handleCloseForm}
              onSubmit={handleSubmitStudent}
              onNotice={(message, severity = "warning") =>
                setAlert({ open: true, message, severity })
              }
              isEdit={false}
            />
          )}

          <SearchFilter
            searchPlaceholder="search"
            filters={filters}
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            dataDownload={true}
            downloadRows={filteredData}
            downloadColumns={headers
              .filter((header) => header.key !== "actions")
              .map((header) => ({
                key: header.key,
                label: header.value,
              }))}
            downloadFileName="school-students"
          />

          <Suspense fallback={<div className="loader"></div>}>
            <SchoolDynamicTable
              headers={headers}
              data={filteredData}
              loading={isLoading}
              expandedRow={expandedRow}
              onRowClick={handleRowClick}
              renderExpandedRow={renderExpandedRow}
              profileImageColumn="Names"
            />
          </Suspense>
            </>
          )}
        </div>
      </Box>
      <Footer />
    </Box>
  );
}
